import React from 'react';
import {
  ButtonMain,
  Dropdown,
  InputLine,
  Radio,
  Selection,
} from '@quero-delivery/quero-components-web';
import { Controller, useForm, useWatch, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { Divider } from 'semantic-ui-react';
import styles from './MemberRegistrationForm.module.scss';
import { celularLabel, cpfLabel } from '@/Utils/FnUtils';
import { ROLE_TYPES } from '@/Interfaces/Data';
import BoxCheckbox from '@/Components/Members/BoxCheckbox';
import { useCities } from '@/_hooks/use-cities';

// TODO Refactor this file, separate domain logic from UI logic
const fetchMembersFormSchema = yup.object({
  name: yup.string().required(),
  cpf: yup.string().required(),
  phone: yup.string().required(),
  email: yup.string().email().required(),
  isEdit: yup.boolean().optional(),
  password: yup.string().when('isEdit', {
    is: false,
    then: yup.string().required(),
  }),
  type: yup.string().oneOf(Object.values(ROLE_TYPES)).required(),
  city: yup.array().of(yup.string()).optional(),
  status: yup.boolean().required(),
  permissions: yup.object({
    delivery: yup.object({
      all: yup.boolean(),
      associateDelivery: yup.boolean(),
      deliveryStatus: yup.boolean(),
      reopenDelivery: yup.boolean(),
      cancelDelivery: yup.boolean(),
      events: yup.boolean(),
    }),
    deliveryman: yup.object({
      all: yup.boolean(),
      createDeliveryman: yup.boolean(),
      editDeliveryman: yup.boolean(),
      approvePreRegister: yup.boolean(),
      blockDeliveryman: yup.boolean(),
      dedicateDeliveryman: yup.boolean(),
    }),
    place: yup.object({
      all: yup.boolean(),
      editPlace: yup.boolean(),
      credit: yup.boolean(),
      debit: yup.boolean(),
      createAndEditFlare: yup.boolean(),
      dedicateOrder: yup.boolean(),
    }),
    city: yup.object({
      all: yup.boolean(),
      editFare: yup.boolean(),
    }),
  }),
});

export type TypePostMemberForm = yup.InferType<typeof fetchMembersFormSchema>;

type Props = {
  values: TypePostMemberForm;
  onSubmit: (params: TypePostMemberForm) => void;
  loading: boolean;
};

const MemberRegistrationForm: React.FC<React.PropsWithChildren<Props>> = ({
  values,
  onSubmit,
  loading,
}) => {
  const navigate = useNavigate();

  const { CITIES_OPTIONS, loadingCities } = useCities();

  const methods = useForm<TypePostMemberForm>({
    resolver: yupResolver(fetchMembersFormSchema),
    defaultValues: values,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const typeAccount = useWatch({
    control,
    name: 'type',
  });

  const renderLogistic = typeAccount === 'LOGISTIC_OPERATOR';

  type OptionsProps = {
    key: string;
    text: string;
    value: string;
  };

  const STATUS_OPTIONS_DELIVERY: Array<OptionsProps> = [
    {
      key: 'all',
      text: 'Todos',
      value: 'all',
    },
    {
      key: 'associateDelivery',
      text: 'Associação de pedido',
      value: 'associateDelivery',
    },
    {
      key: 'deliveryStatus',
      text: 'Status do pedido',
      value: 'deliveryStatus',
    },
    {
      key: 'reopenDelivery',
      text: 'Reabertura de pedido',
      value: 'reopenDelivery',
    },
    {
      key: 'cancelDelivery',
      text: 'Cancelamento de pedido',
      value: 'cancelDelivery',
    },
    {
      key: 'events',
      text: 'Acompanhar eventos',
      value: 'events',
    },
  ];

  const STATUS_OPTIONS_DELIVERY_MAN: Array<OptionsProps> = [
    {
      key: 'all',
      text: 'Todos',
      value: 'allMan',
    },
    {
      key: 'createDeliveryman',
      text: 'Cadastro entregador',
      value: 'createDeliveryman',
    },
    {
      key: 'editDeliveryman',
      text: 'Edição entregador',
      value: 'editDeliveryman',
    },
    {
      key: 'approvePreRegister',
      text: 'Aprovação/reprovação pré-cadastro',
      value: 'approvePreRegister',
    },
    {
      key: 'blockDeliveryman',
      text: 'Bloqueio de entregador',
      value: 'blockDeliveryman',
    },
    {
      key: 'dedicateDeliveryman',
      text: 'Dedicação de entregador',
      value: 'dedicateDeliveryman',
    },
  ];

  const STATUS_OPTIONS_CITY: Array<OptionsProps> = [
    {
      key: 'all',
      text: 'Todos',
      value: 'allPlace',
    },
    {
      key: 'editFare',
      text: 'Edição de tarifa',
      value: 'editFare',
    },
  ];

  const STATUS_OPTIONS_PLACE: Array<OptionsProps> = [
    {
      key: 'all',
      text: 'Todos',
      value: 'allPlace',
    },
    {
      key: 'editPlace',
      text: 'Editar cadastro',
      value: 'editPlace',
    },
    {
      key: 'credit',
      text: 'Lançamento de crédito',
      value: 'credit',
    },
    {
      key: 'debit',
      text: 'Lançamento de débito',
      value: 'debit',
    },
    {
      key: 'createAndEditFlare',
      text: 'Criação e edição de tarifa',
      value: 'createAndEditFlare',
    },
    {
      key: 'dedicateOrder',
      text: 'Dedicação de pedido',
      value: 'dedicateOrder',
    },
  ];

  return (
    <div className="container">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.filterContainer}>
            <div className={styles.formGroup}>
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLine
                    value={value}
                    errorMessage={errors.name?.message}
                    label="Nome"
                    onChange={event => onChange(event.target.value)}
                  />
                )}
              />

              <Controller
                name="cpf"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLine
                    value={value}
                    errorMessage={errors.cpf?.message}
                    label="CPF"
                    onChange={event => onChange(cpfLabel(event.target.value))}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLine
                    value={value}
                    errorMessage={errors.phone?.message}
                    label="Celular"
                    onChange={event =>
                      onChange(celularLabel(event.target.value))
                    }
                  />
                )}
              />
            </div>

            <div className={styles.formGroup}>
              <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLine
                    value={value}
                    errorMessage={errors.email?.message}
                    label="E-mail"
                    onChange={event => onChange(event.target.value)}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLine
                    type="password"
                    value={value}
                    errorMessage={errors.password?.message}
                    label="Senha"
                    onChange={event => onChange(event.target.value)}
                    autoComplete="new-password"
                  />
                )}
              />
            </div>

            <div>
              <span>Tipo de conta</span>
              <Divider />
              <div className={styles.formGroup}>
                <div>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className={styles.containerRadioTipoConta}>
                        <Radio
                          label="Administrador"
                          checked={value === 'ADMIN'}
                          onChange={() => onChange('ADMIN')}
                        />
                        <Radio
                          label="Operador logístico"
                          checked={value === 'LOGISTIC_OPERATOR'}
                          onChange={() => onChange('LOGISTIC_OPERATOR')}
                        />
                        <Radio
                          label="Suporte"
                          checked={value === 'SUPPORT'}
                          onChange={() => onChange('SUPPORT')}
                        />
                      </div>
                    )}
                  />
                </div>

                {renderLogistic && (
                  <Controller
                    name="city"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        label="Município"
                        options={CITIES_OPTIONS}
                        loading={loadingCities}
                        multiple
                        value={value}
                        search
                        placeholder="Selecione o município"
                        onChange={(ev, data) => onChange(data.value as string)}
                      />
                    )}
                  />
                )}
              </div>
            </div>

            {renderLogistic && (
              <div>
                <span>Permissões</span>
                <Divider />
                <div className={styles.permissionsContainer}>
                  <div className={styles.permissionsRow}>
                    <BoxCheckbox
                      options={STATUS_OPTIONS_DELIVERY}
                      title="Entrega"
                      objName="delivery"
                      control={control}
                    />
                    <BoxCheckbox
                      options={STATUS_OPTIONS_DELIVERY_MAN}
                      title="Entregadores"
                      objName="deliveryman"
                      control={control}
                    />
                    <BoxCheckbox
                      options={STATUS_OPTIONS_PLACE}
                      title="Estabelecimento"
                      objName="place"
                      control={control}
                    />
                  </div>
                  <BoxCheckbox
                    options={STATUS_OPTIONS_CITY}
                    title="Município"
                    objName="city"
                    control={control}
                    className={styles.permissionsCity}
                  />
                </div>
              </div>
            )}
            <div>
              <span>Status do colaborador</span>
              <Divider />

              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Selection
                    type="switch"
                    checked={value}
                    onChange={event => {
                      event.preventDefault();
                      onChange(!value);
                    }}
                    className="switch-with-colors"
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.footerButtons}>
            <ButtonMain
              secondary
              type="button"
              textFirst="Cancelar"
              onClick={() => navigate('/equipe')}
            />
            <ButtonMain textFirst="Salvar" type="submit" disabled={loading} />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default MemberRegistrationForm;
